import { useCallback, useEffect } from 'react';
import {
  login,
  LoginForm as LoginFormInterface,
  ConfirmLogin as ConfirmLoginInterface,
  useApi,
} from '@repo-level/core-front';

import { AnalyticsEventName } from 'api/analytics';
import { useAnalytics } from 'helpers/analytics';
import { localStorageEmail } from 'helpers/common';
import {
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS,
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL,
} from 'helpers/constants';

export const ERRORS_COUNT_STORAGE_NAME = 'login';

export const saveErrorsCountToStorage =
  AUTH_RATE_LIMIT_LOGIN_ATTEMPTS && AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL
    ? {
        storageName: ERRORS_COUNT_STORAGE_NAME,
        timeLeftToClear: AUTH_RATE_LIMIT_LOGIN_ATTEMPTS_TTL,
      }
    : undefined;

export const useLogin = () => {
  const { sendEventToAnalytics } = useAnalytics();

  const [
    requestLogin,
    { data: userData, error: loginError, pending: loginPending, requestCount },
    { resetRequestCount },
  ] = useApi(login, undefined, { saveErrorsCountToStorage });

  const handleLogin = useCallback(
    async (values: LoginFormInterface | ConfirmLoginInterface) => {
      const { response } = await requestLogin(values).request;

      if (response) {
        resetRequestCount();
        localStorageEmail.push('');
      }

      if (response?.data.id != null) {
        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__LOGIN__FORM__SUCCESS,
        });
      }

      return { response };
    },
    [requestLogin, resetRequestCount, sendEventToAnalytics]
  );

  useEffect(() => {
    if (loginError) {
      const errorJSON = JSON.stringify(loginError);
      const attributes = { additionalData: errorJSON };

      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__LOGIN__FORM__ERROR,
        attributes,
      });
    }
  }, [loginError, sendEventToAnalytics]);

  return {
    handleLogin,
    userData,
    loginError,
    loginPending,
    requestCount,
  };
};
